/* eslint-disable @typescript-eslint/no-unused-vars */
import {Button} from '@mui/material';
import {TypeLocalNameEnum} from 'domain/usecases';
import {WellBornTaggingEnum} from 'main/analytics/gaTagsEnum';
import BeBornWellImgDesktop from 'presentation/assets/images/img-nascer-bem-desktop@2x.webp';
import {Loading} from 'presentation/components/Loading/Loading';
import {QueryParamsForTaggingEnum} from 'presentation/constants';
import Icon from 'presentation/icons';
import {useQueryParams} from 'presentation/pages/private/hooks';
import {translations} from 'presentation/translations-new';
import type {Specialty} from 'presentation/utils/categorizeSpecialtiesByFirstLetter';
import React, {Fragment, useEffect, useState} from 'react';
import {useBookingSpecialty} from '../../hooks';
import {useWellBornEligibility} from '../../hooks/useWellBornEligibility';

import {BeBornWellCard} from '../BeBornWellCard';
import {BeBornWellDrawer} from '../BeBornWellDrawer';

import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {useHideSearchBarStore} from 'presentation/modules/booking/shared/store/useHideSearchBarStore';

import {CheckBookingModalEnum} from '../../hooks/useBookingSpecialty/types';
import {BookingNotFoundState} from '../BookingNotFoundState';
import {BookingNotFoundStateVariantEnum} from '../BookingNotFoundState/types';
import {PendingDialog} from '../PendingDialog';
import {
	DescriptionTypography,
	StyledCard,
	StyledCardContent,
	StyledCardMedia,
	TitleTypography,
} from './styles';
import {BeBornWellNotRegisterProps} from './types';
import {useHideStepBarStore} from 'presentation/modules/booking/shared/store/useHideStepBarStore';
import {getWellBornEligibility} from 'presentation/modules/booking/shared/services/getWellBornEligibility';

const {beBornWellStep, buttons} =
	translations['pt-br'].privateFlow.bookingFlow.beBornWellFlow.beBornWellDrawer;
const {verifyingAuth} = translations['pt-br'].privateFlow.bookingFlow.specialtyFlow;
const {loading} = translations['pt-br'].commons;

export function BeBornWellNotRegister({
	flowType,
	selectedSpecialty,
	handleNext,
}: BeBornWellNotRegisterProps) {
	const setHideSearchBarState = useHideSearchBarStore(state => state.setHideSearchBar);
	const setHideStepBarState = useHideStepBarStore(state => state.setHideStepBar);
	const {setSearchParams} = useQueryParams();

	const [selectedSpecialtyState, setSelectedSpecialtyState] = useState<Specialty | null>(
		selectedSpecialty || null,
	);
	const [showBeBornWellCard, setShowBeBornWellCard] = useState(false);
	const [openBeBornWellDrawer, setOpenBeBornWellDrawer] = useState(false);
	const [isProcessing, setIsProcessing] = useState(false);
	const bookingData = useBookingStore(state => state.bookingData);
	const {ufCode, cityName, typeLocalName, isManualLocation} = bookingData;
	const {setEligibilityData} = useWellBornEligibility();
	const {handleSelectCard, checkBookingModal} = useBookingSpecialty({
		flowType,
		handleNext: handleNext || (() => {}),
	});
	const isUsingGeolocation = !isManualLocation;

	const handleRegisterClick = async (specialty: Specialty) => {
		setSearchParams(prev => {
			prev.delete(QueryParamsForTaggingEnum.SHOW_BE_BORN_WELL_NOT_REGISTER);
			return prev;
		});
		setIsProcessing(true);
		setSelectedSpecialtyState(specialty);
		try {
			const eligibilityResponse = await getWellBornEligibility({
				stateCode: ufCode,
				typeLocalName: isUsingGeolocation
					? TypeLocalNameEnum.CIDADE
					: (typeLocalName ?? TypeLocalNameEnum.CIDADE),
				localName: cityName,
				specialtyCode: specialty.code.toString(),
			});

			if (!eligibilityResponse) {
				await handleSelectCard(specialty);
				setIsProcessing(false);
				return;
			}

			setEligibilityData(eligibilityResponse);
			setOpenBeBornWellDrawer(true);
		} catch (error) {
			await handleSelectCard(specialty);
		} finally {
			setIsProcessing(false);
		}
	};

	const handleNotPregnantClick = () => {
		setOpenBeBornWellDrawer(false);
		if (selectedSpecialtyState) {
			handleSelectCard(selectedSpecialtyState);
		}
	};

	const handleShowBeBornWellCard = () => {
		setOpenBeBornWellDrawer(false);
		setShowBeBornWellCard(true);
	};

	const handleContinueToBooking = () => {
		if (selectedSpecialtyState) {
			handleSelectCard(selectedSpecialtyState);
		}
	};

	useEffect(() => {
		setHideSearchBarState(true);
		setHideStepBarState(true);
	}, []);

	const loadingText =
		checkBookingModal === CheckBookingModalEnum.VERIFYING_AUTH ? verifyingAuth : loading;

	return (
		<Fragment>
			{openBeBornWellDrawer ? (
				<BeBornWellDrawer
					open={openBeBornWellDrawer}
					setOpen={setOpenBeBornWellDrawer}
					handleNotPregnant={handleNotPregnantClick}
					handleContinueToBooking={handleContinueToBooking}
					onShowBeBornWellCard={handleShowBeBornWellCard}
					onShowBeBornWellNotRegister={() => {}}
				/>
			) : null}

			{showBeBornWellCard ? (
				<BeBornWellCard flowType={flowType} continueToBooking={handleContinueToBooking} />
			) : null}

			{!showBeBornWellCard ? (
				<Fragment>
					<BookingNotFoundState
						flowType={flowType}
						selectedSpecialty={selectedSpecialty}
						handleNext={handleNext}
						variant={BookingNotFoundStateVariantEnum.BE_BORN_WELL}
						dataTagId={WellBornTaggingEnum.VISUALIZOU_CARD_DE_RECUSA}
						showHeader={false}
					/>
					<StyledCard
						data-tag-id={
							WellBornTaggingEnum.VISUALIZOU_CARD_QUERO_ME_INSCREVER_APOS_RECUSA
						}>
						<StyledCardMedia
							component="img"
							image={BeBornWellImgDesktop}
							alt="BeBornWellCard"
						/>
						<StyledCardContent>
							<TitleTypography>{beBornWellStep.title}</TitleTypography>
							<DescriptionTypography>
								{beBornWellStep.description}
							</DescriptionTypography>
							<Button
								data-tag-id={
									WellBornTaggingEnum.CLICK_QUERO_ME_INSCREVER_APOS_RECUSA
								}
								variant="text"
								onClick={() => {
									if (selectedSpecialty) {
										handleRegisterClick(selectedSpecialty);
									}
								}}
								disabled={isProcessing}
								endIcon={
									isProcessing ? (
										<Loading color="inherit" size={16} />
									) : (
										<Icon name="arrow-right" size={20} />
									)
								}>
								{buttons.register}
							</Button>
						</StyledCardContent>
					</StyledCard>
				</Fragment>
			) : null}

			<PendingDialog
				open={
					checkBookingModal === CheckBookingModalEnum.VERIFYING_AUTH ||
					checkBookingModal === CheckBookingModalEnum.VERIFYING_DUPLICITY ||
					checkBookingModal === CheckBookingModalEnum.VERIFYING_DISTRICTS ||
					checkBookingModal === CheckBookingModalEnum.VERIFYING_REQUEST_SCHEDULE
				}
				text={loadingText}
			/>
		</Fragment>
	);
}
